import React, { useState, useEffect } from 'react';
import { MapPin, Satellite, Camera, Coins, TrendingUp, Users, Globe, ChevronDown, Menu, X, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import LoginModal from './Login';
import RegisterModal from './register';
import Header from './Header';

export default function WasteManagementLanding() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStat, setCurrentStat] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentStat(prev => (prev + 1) % 3);
    }, 3000);

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    { number: "2.5M", label: "Tons of waste generated daily in Lagos", icon: Globe },
    { number: "400+", label: "Illegal dumpsites identified", icon: MapPin },
    { number: "85%", label: "Carbon emission reduction potential", icon: TrendingUp }
  ];

  const handleSwitchToRegister = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const handleSwitchToLogin = () => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  const closeAllModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans overflow-hidden">
      <Header 
        onShowLogin={() => setShowLoginModal(true)}
        onShowRegister={() => setShowRegisterModal(true)}
      />

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 md:pt-20">
        {/* Enhanced Background with Gradient Mesh */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-emerald-25 to-teal-50">
          <div className="absolute inset-0 bg-gradient-to-r from-green-400/5 to-emerald-400/5"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent_70%)]"></div>
        </div>

        {/* Floating Elements Animation */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-full blur-sm animate-pulse ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 100 + 20}px`,
                height: `${Math.random() * 100 + 20}px`,
                animationDelay: `${i * 200}ms`,
                animationDuration: `${3 + Math.random() * 2}s`,
                transform: `translateY(${scrollY * 0.1}px)`,
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            
            {/* Logo */}
            <div className="mb-8 mt-4 sm:mb-12">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-2">
                <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Waste</span>
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Chain</span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto rounded-full"></div>
              <small style={{"fontSize":8}}>Powered By: <b className='text-bold '>Climate Technology Solutions</b></small>
            </div>

            {/* Main Headline */}
            <div className="mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 sm:mb-8">
                <span style={{"fontSize":22}} className="block mb-2 sm:mb-4 text-sm text-bold text-gray-900">Transforming Cities Through</span>
                <span className="bg-gradient-to-r  from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  AI & Blockchain Powered
                </span> &nbsp;
                <br /> 
                <span className="bg-gradient-to-r  from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Waste Management
                </span>
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
                Combating illegal dumpsites with satellite imaging, machine learning, and community engagement. 
                Reducing carbon emissions while rewarding citizens for environmental stewardship.
              </p>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="mb-16 sm:mb-20 flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4">
              <button
                onClick={() => setShowLoginModal(true)} 
                className="group relative w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-green-500/25 overflow-hidden">
                <span className="relative z-10">See Demo</span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
              <a href='#about' className="group w-full sm:w-auto border-2 border-green-600 text-green-600 px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-green-600 hover:text-white transition-all duration-300 transform hover:scale-105">
                Learn More
                <ChevronDown className="inline ml-2 w-5 h-5 group-hover:animate-bounce" />
              </a>
            </div>

            {/* Scroll Indicator */}
            {/* <div className="animate-bounce">
              <ChevronDown className="w-8 h-8 mx-auto text-green-600 opacity-60" />
            </div> */}
          </div>
        </div>
      </div>

      {/* Enhanced Stats Section */}
      <div className="py-16 sm:py-20 bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(34,197,94,0.1)_50%,transparent_75%)] bg-[length:20px_20px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className={`text-center group cursor-pointer transform transition-all duration-500 hover:scale-110 ${
                    currentStat === index ? 'scale-110' : 'scale-100'
                  }`}
                >
                  <div className={`relative inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full mb-6 transition-all duration-500 ${
                    currentStat === index 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-2xl shadow-green-500/50' 
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 group-hover:shadow-xl group-hover:shadow-green-600/50'
                  }`}>
                    <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                    <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                  </div>
                  <div className={`text-4xl sm:text-5xl font-black mb-4 transition-all duration-500 ${
                    currentStat === index 
                      ? 'bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent' 
                      : 'bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent'
                  }`}>
                    {stat.number}
                  </div>
                  <p className="text-gray-300 text-lg sm:text-xl font-medium px-2">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Enhanced Problem Statement */}
      <div className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              The Challenge in Lagos
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-orange-500 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Nigeria's megacity faces a waste crisis that threatens public health, environment, and quality of life
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              {[
                {
                  title: "Irregular Waste Collection",
                  description: "Missing collection schedules lead to roadside waste accumulation, creating breeding grounds for illegal dumping."
                },
                {
                  title: "Scavenging Activities", 
                  description: "Informal waste pickers extract valuables but abandon the rest, creating unregistered dumpsites across the city."
                },
                {
                  title: "Flood-Induced Dumpsites",
                  description: "Heavy rains relocate waste to new areas, which then become permanent illegal dumping grounds."
                }
              ].map((item, index) => (
                <div key={index} className="group flex items-start space-x-4 sm:space-x-6 p-4 sm:p-6 rounded-2xl hover:bg-white hover:shadow-xl transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-red-100 to-orange-100 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"></div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-base sm:text-lg leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="order-1 lg:order-2">
              <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-3xl p-6 sm:p-8 lg:p-12 text-white shadow-2xl transform hover:scale-105 transition-all duration-300">
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  The Result?
                </h3>
                <ul className="space-y-4 sm:space-y-6 text-base sm:text-lg">
                  {[
                    "Thousands of unregistered dumpsites",
                    "Massive carbon emissions", 
                    "Public health hazards",
                    "Environmental degradation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-center group cursor-pointer">
                      <span className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mr-4 group-hover:scale-125 transition-transform duration-200"></span>
                      <span className="group-hover:text-green-400 transition-colors duration-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Solution Section */}
      <div id="about" className="py-16 sm:py-20 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_75%,rgba(34,197,94,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_25%,rgba(16,185,129,0.1),transparent_50%)]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              Our AI-Powered Solution
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              A comprehensive platform that identifies, tracks, and manages illegal dumpsites while rewarding community participation
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: Satellite, title: "Satellite Imaging", description: "Real-time detection of new dumpsites using advanced satellite imagery and AI analysis." },
              { icon: Camera, title: "Community Reports", description: "Citizens capture and submit evidence of illegal dumpsites through our mobile platform." },
              { icon: TrendingUp, title: "ML Carbon Analysis", description: "Machine learning algorithms estimate carbon emissions from each dumpsite for policy decisions." },
              { icon: Coins, title: "Crypto Rewards", description: "Citizens earn cryptocurrency tokens for reporting and monitoring dumpsites effectively." }
            ].map((item, index) => (
              <div key={index} className="group bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-2xl hover:shadow-green-500/20 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 border border-white/50">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mb-6 group-hover:shadow-xl group-hover:shadow-green-500/50 transition-all duration-300">
                  <item.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900 group-hover:text-green-600 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced Impact Section */}
      <div className="py-16 sm:py-20 bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_25%,rgba(34,197,94,0.1)_50%,transparent_75%)] bg-[length:40px_40px] animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Measurable Impact
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-green-400 to-emerald-400 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Data-driven results that transform cities and improve lives
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
              {[
                { title: "Environmental", description: "Reduce carbon emissions by up to 85% through systematic waste management and early intervention.", color: "from-green-600 to-emerald-600" },
                { title: "Economic", description: "Create income opportunities for citizens while reducing government cleanup costs by 60%.", color: "from-blue-600 to-cyan-600" },
                { title: "Social", description: "Improve public health outcomes and community engagement in environmental stewardship.", color: "from-purple-600 to-pink-600" }
              ].map((item, index) => (
                <div key={index} className={`group bg-gradient-to-r ${item.color} p-6 sm:p-8 rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 cursor-pointer`}>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-100 text-base sm:text-lg leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="order-1 lg:order-2">
              <div className="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 p-8 sm:p-12 rounded-3xl text-center shadow-2xl transform hover:scale-105 transition-all duration-300 hover:shadow-green-500/50">
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-12 text-white">
                  Ready to Scale
                </h3>
                <div className="space-y-6 sm:space-y-8">
                  {[
                    { location: "Lagos", status: "Current pilot city" },
                    { location: "Nigeria", status: "National expansion phase" },
                    { location: "Africa", status: "Continental vision" }
                  ].map((item, index) => (
                    <div key={index} className="group cursor-pointer">
                      <div className="text-3xl sm:text-4xl lg:text-5xl font-black text-white group-hover:text-green-200 transition-colors duration-300">
                        {item.location}
                      </div>
                      <div className="text-green-100 text-base sm:text-lg mt-2 group-hover:text-white transition-colors duration-300">
                        {item.status}
                      </div>
                      {index < 2 && <div className="w-full h-px bg-green-400 opacity-30 mt-6 sm:mt-8 group-hover:opacity-60 transition-opacity duration-300"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced CTA Section */}
      <div id="contact" className="py-16 sm:py-20 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.05),transparent_50%)]"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8">
            Join the Waste Revolution
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl mb-8 sm:mb-12 opacity-95 max-w-4xl mx-auto leading-relaxed">
            Partner with us to transform urban waste management infrastructures across Africa and beyond
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center max-w-lg mx-auto">
            <a 
              href='mailto:info.climatechnologysolutions@gmail.com' 
              className="group bg-white text-green-600 px-8 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-white/30 relative overflow-hidden"
            >
              <span className="relative z-10">Partner With Us</span>
              <div className="absolute inset-0 bg-gradient-to-r from-gray-100 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <button
              onClick={() => setShowLoginModal(true)}
              className="group border-2 border-white text-white px-8 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-white hover:text-green-600 transition-all duration-300 transform hover:scale-105 relative overflow-hidden"
            >
              <span className="relative z-10">See Demo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Footer */}
      <div className="py-12 sm:py-16 bg-gradient-to-b from-white to-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-12 sm:w-20 sm:h-14 bg-gradient-to-r from-black to-gray-800 rounded-full flex items-center justify-center mr-4 shadow-xl">
              <img src='img/logo1.png' className="w-14 h-10 sm:w-16 sm:h-12 object-contain" alt="Logo" />
            </div>
            <span className="text-2xl sm:text-3xl font-bold">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Waste</span>
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Chain</span>
            </span>
          </div>
          <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-emerald-600 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base sm:text-lg font-medium">
            Powered By Climate Technology Solutions
          </p>
        </div>
      </div>

      {/* Modals */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={closeAllModals}
        onSwitchToRegister={handleSwitchToRegister}
      />
      <RegisterModal 
        isOpen={showRegisterModal} 
        onClose={closeAllModals}
        onSwitchToLogin={handleSwitchToLogin}
      />
    </div>
  );
}